import { useCFPValues } from '@bcp-channels-sharedcomponents-reactjs/cf-proxy';
import { Box, Button, ComposableTypography, Link } from '@bcp-nextgen-dx-component-factory/design-system';
import { ArrowRight } from '@bcp-nextgen-dx-component-factory/icons-accolade';
import { UIHelper } from '@helpers';
import { SUBTITLE_100_02, SUBTITLE_100_04 } from '@types';

import { ComposableTypographyWithTooltip } from '../composableTypographyWithTooltip';
import { CollapsibleBlockProps } from './collapsibleBlock.types';

/* Generic component used in the login screen.
   It has two layout behaviors (open and closed, controlled by the "isOpen" prop).
   Any prop is dynamic and will render if the value is provided.
   There's also a "openContent.children" prop that allows more dynamic ReactNode content.
*/

export const CollapsibleBlock = ({ isOpen, openContent, closedContent, extraContent }: CollapsibleBlockProps) => {
	const { getToken } = useCFPValues();

	const openNode = (
		<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(32) }}>
			{openContent?.label?.value && (
				<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(8) }}>
					<Box styles={{ alignItems: 'start' }}>
						<ComposableTypographyWithTooltip
							text={openContent.label.value}
							baseProps={{
								appearance: getToken({
									default: {
										default: SUBTITLE_100_04,
										mobile: SUBTITLE_100_04,
										tablet: SUBTITLE_100_04,
										laptop: 'subtitle-200-02',
									},
								}),
							}}
							tags={{}}
							tooltip={
								openContent?.label.tooltip
									? {
											title: openContent?.label?.tooltip?.title,
											description: openContent?.label?.tooltip?.description,
									  }
									: undefined
							}
						/>
					</Box>

					{openContent?.links?.map(
						(link, index) =>
							link.label && (
								<Box key={link.label + index} styles={{ alignSelf: 'start' }}>
									<Link
										label={link.label}
										disabled={link.disabled}
										onPress={link.onClick}
										link={link.href}
									/>
								</Box>
							)
					)}
				</Box>
			)}

			{openContent?.children}

			{openContent?.button?.label && (
				<Box styles={{ alignSelf: 'start' }}>
					<Button
						endContent={ArrowRight.Outline}
						disabled={openContent.button.disabled}
						onPress={openContent.button.onClick}
						size="large"
						type="submit"
					>
						{openContent.button.label}
					</Button>
				</Box>
			)}
		</Box>
	);

	const closedNode = closedContent ? (
		<Box styles={{ flexDirection: 'column', gap: UIHelper.rem(8) }}>
			<Box styles={{ flexWrap: 'wrap', columnGap: UIHelper.rem(8) }}>
				{closedContent.label && (
					<ComposableTypography
						text={closedContent.label}
						baseProps={{
							appearance: getToken({
								default: {
									default: SUBTITLE_100_04,
									mobile: SUBTITLE_100_04,
									tablet: SUBTITLE_100_04,
									laptop: 'subtitle-200-02',
								},
							}),
							styles: {
								whiteSpace: 'nowrap',
							},
						}}
						tags={{}}
					/>
				)}

				{closedContent.value && (
					<ComposableTypography
						text={closedContent.value}
						baseProps={{
							appearance: getToken({
								default: {
									default: SUBTITLE_100_02,
									mobile: SUBTITLE_100_02,
									tablet: SUBTITLE_100_02,
									laptop: 'subtitle-200-03',
								},
							}),
						}}
						tags={{}}
					/>
				)}
			</Box>

			{closedContent.link?.label && (
				<Box styles={{ alignSelf: 'start' }}>
					<Link
						label={closedContent.link.label}
						disabled={closedContent.link.disabled}
						onPress={closedContent.link.onClick}
					/>
				</Box>
			)}
		</Box>
	) : null;

	return isOpen ? (
		<>
			{openNode}
			{extraContent}
		</>
	) : (
		<>
			{closedNode}
			{extraContent}
		</>
	);
};
